
export class IntrashBackend {

    static isDeployed() {
        const e = process.env.NODE_ENV;
        return e !== "development" && e !== "test";
    }

    static getServerHostname() {
        if (IntrashBackend.isDeployed()) {
            return "intrashtenimento.berianz.net";
        }
        else {
            return "127.0.0.1";
        }
    }

    static getBackendURL() {
        if (IntrashBackend.isDeployed()) {
            return "https://" + IntrashBackend.getServerHostname() + "/api";
        }
        else {
            return "http://" + IntrashBackend.getServerHostname() + ":30050";
        }
    }

    static async getLoggedInUser() {
        // Get the session token
        const token = localStorage.getItem("sessionToken")
        if (token === null) return null

        // Request the session user
        const url = IntrashBackend.getBackendURL() + "/session?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url);
        const responseJson = await response.json();

        // Parse the response
        if (responseJson.status === "ok") {
            // Refresh the session information
            const user = responseJson.content.user;
            localStorage.setItem("sessionUserId", user.id);
            localStorage.setItem("sessionCanEditUsers", user.canEditUsers);
            localStorage.setItem("sessionCanEditMovies", user.canEditMovies);
            localStorage.setItem("sessionCanVoteMovies", user.canVoteMovies);

            // Return the log-in user
            return user;
        }
        else {
            // Clear the session information
            localStorage.removeItem("sessionToken");
            localStorage.removeItem("sessionUserId");
            localStorage.removeItem("sessionCanEditUsers");
            localStorage.removeItem("sessionCanEditMovies");
            localStorage.removeItem("sessionCanVoteMovies");

            // The log-in is not valid
            return null;
        }
    }

    static async login(loginUsername, loginPassword) {
        // Request a login
        const url = IntrashBackend.getBackendURL() + "/session";
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                username: loginUsername,
                password: loginPassword
            })
        });
        const responseJson = await response.json();

        // Parse the response
        if (responseJson.status === "ok") {
            // The log-in has succeeded
            const session = responseJson.content.session;
            const user = responseJson.content.user;
            localStorage.setItem("sessionToken", session.token);
            localStorage.setItem("sessionUserId", session.userId);
            localStorage.setItem("sessionCanEditUsers", user.canEditUsers);
            localStorage.setItem("sessionCanEditMovies", user.canEditMovies);
            localStorage.setItem("sessionCanVoteMovies", user.canVoteMovies);

            // Return the log-in user
            return user;
        }
        else {
            // The log-in has failed
            localStorage.removeItem("sessionToken");
            localStorage.removeItem("sessionUserId");
            localStorage.removeItem("sessionCanEditUsers");
            localStorage.removeItem("sessionCanEditMovies");
            localStorage.removeItem("sessionCanVoteMovies");

            // Return null
            return null;
        }
    }

    static async logout() {
        // Request a logout
        const url = IntrashBackend.getBackendURL() + "/session?token=" + localStorage.getItem("sessionToken");
        await fetch(url, { method: "DELETE" });
        
        // Clear the session data
        localStorage.removeItem("sessionToken");
        localStorage.removeItem("sessionUserId");
        localStorage.removeItem("sessionCanEditUsers");
        localStorage.removeItem("sessionCanEditMovies");
        localStorage.removeItem("sessionCanVoteMovies");
    }

    static async getLoggedInUsers() {
        // Request the users' list
        const url = IntrashBackend.getBackendURL() + "/logged-users?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url);
        const responseJson = await response.json();

        // Parse the response
        if (responseJson.status === "ok") {
            return responseJson.content.users;
        }
        else {
            return [];
        }
    }

    static async getUsers() {
        // Request the users' list
        const url = IntrashBackend.getBackendURL() + "/users?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url);
        const responseJson = await response.json();

        // Parse the response
        if (responseJson.status === "ok") {
            return responseJson.content.users;
        }
        else {
            return [];
        }
    }

    static async deleteUser(userId) {
        // Request the delete
        const url = IntrashBackend.getBackendURL() + "/users?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                users: [
                    userId
                ]
            })
        });
        const responseJson = await response.json();

        // Check if the operation has succeeded
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async addUser() {
        // Request the new user
        const url = IntrashBackend.getBackendURL() + "/users?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                users: [ {} ]
            })
        });
        const responseJson = await response.json();

        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;
        
        // Return the new user
        return responseJson.content.users[0];
    }

    static async changeUsername(userId, newUsername) {
        // Request a login
        const url = IntrashBackend.getBackendURL() + "/user-usernames?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userUsernames: [
                    {
                        userId: userId,
                        username: newUsername
                    }
                ]
            })
        });
        const responseJson = await response.json();

        // Check if the operation has succeeded
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async changePassword(userId, newPassword) {
        // Request a login
        const url = IntrashBackend.getBackendURL() + "/user-credentials?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userCredentials: [
                    {
                        userId: userId,
                        password: newPassword
                    }
                ]
            })
        });
        const responseJson = await response.json();

        // Check if the operation has succeeded
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async changeCanEditUsers(userId, newCanEditUsers) {
        // Request a login
        const url = IntrashBackend.getBackendURL() + "/user-permissions?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userPermissions: [
                    {
                        userId: userId,
                        canEditUsers: newCanEditUsers
                    }
                ]
            })
        });
        const responseJson = await response.json();

        // Check if the operation has succeeded
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async changeCanEditMovies(userId, newCanEditMovies) {
        // Request a login
        const url = IntrashBackend.getBackendURL() + "/user-permissions?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userPermissions: [
                    {
                        userId: userId,
                        canEditMovies: newCanEditMovies
                    }
                ]
            })
        });
        const responseJson = await response.json();

        // Check if the operation has succeeded
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async changeCanVoteMovies(userId, newCanVoteMovies) {
        // Request a login
        const url = IntrashBackend.getBackendURL() + "/user-permissions?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userPermissions: [
                    {
                        userId: userId,
                        canVoteMovies: newCanVoteMovies
                    }
                ]
            })
        });
        const responseJson = await response.json();

        // Check if the operation has succeeded
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async getMovies() {
        // Request the movies' list
        var url = IntrashBackend.getBackendURL() + "/movies?token=" + localStorage.getItem("sessionToken");
        let response = await fetch(url);
        let responseJson = await response.json();
        
        // Parse the response
        if (responseJson.status === "ok") {
            return responseJson.content.movies;
        }
        else {
            return [];
        }
    }

    static async deleteMovie(movieId) {
        // Request the delete
        const url = IntrashBackend.getBackendURL() + "/movies?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                movies: [
                    movieId
                ]
            })
        });
        const responseJson = await response.json();

        // Check if the operation has succeeded
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async addMovie(newMovieTitle, newMovieSortTitle, newMovieReleaseYear, newMovieWatched) {
        // Request the new user
        const url = IntrashBackend.getBackendURL() + "/movies?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                movies: [ 
                    {
                        title: newMovieTitle,
                        sortTitle: newMovieSortTitle,
                        releaseYear: parseInt(newMovieReleaseYear),
                        watched: newMovieWatched
                    }
                ]
            })
        });
        const responseJson = await response.json();
        
        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;
        
        // Return the new movie
        return responseJson.content.movies[0];
    }

    static async editMovie(movieId, newMovieTitle, newMovieSortTitle, newMovieReleaseYear, newMovieWatched, clearAllVotes = false) {
        // Request the edit
        const url = IntrashBackend.getBackendURL() + "/movies?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                movies: [ 
                    {
                        id: movieId,
                        title: newMovieTitle,
                        sortTitle: newMovieSortTitle,
                        releaseYear: parseInt(newMovieReleaseYear),
                        watched: newMovieWatched
                    }
                ]
            })
        });
        const responseJson = await response.json();
        
        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;

        // Clear all votes (if necessary)
        if (clearAllVotes) {
            await this.clearMovieVotes(movieId);
        }
        
        // Return the new movie
        return responseJson.content.movies[0];
    }

    static async clearMovieVotes(movieId) {
        // Request the delete
        const url = IntrashBackend.getBackendURL() + "/movie-votes?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                movies: [
                    movieId
                ]
            })
        });
        const responseJson = await response.json();
        
        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async getMovieUserVotes(movieId = null, userId = null) {
        // Build request
        var url = IntrashBackend.getBackendURL() + "/movie-user-votes?token=" + localStorage.getItem("sessionToken");
        if (movieId !== null) url += "&movieId=" + movieId;
        if (userId !== null) url += "&userId=" + userId;

        // Perform the request
        const response = await fetch(url);
        const responseJson = await response.json();
        
        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;
        
        // Return the vote(s)
        return responseJson.content.votes;
    }

    static async setMovieUserVote(votedMovieId, votedVote) {
        // Perform the request
        var url = IntrashBackend.getBackendURL() + "/movie-user-vote?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                movieId: votedMovieId,
                vote: votedVote
            })
        });
        const responseJson = await response.json();
        
        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;
    }
    
    static async getUserReviewedMovies() {
        // Build request
        var url = IntrashBackend.getBackendURL() + "/user-reviewed-movies?token=" + localStorage.getItem("sessionToken");
        
        // Perform the request
        const response = await fetch(url);
        const responseJson = await response.json();
        
        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;
        
        // Return the movies
        return responseJson.content.movies;
    }

    static async getMovieUserReviews(movieId = null, userId = null) {
        // Build request
        var url = IntrashBackend.getBackendURL() + "/movie-user-reviews?token=" + localStorage.getItem("sessionToken");
        if (movieId !== null) url += "&movieId=" + movieId;
        if (userId !== null) url += "&userId=" + userId;

        // Perform the request
        const response = await fetch(url);
        const responseJson = await response.json();
        
        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;
        
        // Return the vote(s)
        return responseJson.content.reviews;
    }

    static async setMovieUserReview(reviewedMovieId, reviewedBrief, reviewedReview) {
        // Perform the request
        var url = IntrashBackend.getBackendURL() + "/movie-user-review?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                movieId: reviewedMovieId,
                brief: reviewedBrief,
                review: reviewedReview
            })
        });
        const responseJson = await response.json();
        
        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async deleteMovieUserReview(reviewedMovieId) {
        // Perform the request
        var url = IntrashBackend.getBackendURL() + "/movie-user-review?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                movieId: reviewedMovieId
            })
        });
        const responseJson = await response.json();
        
        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async getBackups() {
        // Request the backups' list
        const url = IntrashBackend.getBackendURL() + "/backups?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url);
        const responseJson = await response.json();

        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;

        // Return the backups' list
        return responseJson.content.backups;
    }

    static async deleteBackup(backupFileName) {
        // Request the delete
        const url = IntrashBackend.getBackendURL() + "/backups?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                backups: [
                    backupFileName
                ]
            })
        });
        const responseJson = await response.json();

        // Check if the operation has succeeded
        if (responseJson.status !== "ok") throw responseJson.message;
    }

    static async createBackup() {
        // Request the new user
        const url = IntrashBackend.getBackendURL() + "/backups?token=" + localStorage.getItem("sessionToken");
        const response = await fetch(url, { method: "POST" });
        const responseJson = await response.json();

        // Parse the response
        if (responseJson.status !== "ok") throw responseJson.message;
        
        // Return the new backups
        return responseJson.content.backup;
    }

    static levenshteinDistance(str1 = '', str2 = '') {
        const track = Array(str2.length + 1).fill(null).map(() =>
        Array(str1.length + 1).fill(null));
        for (let i = 0; i <= str1.length; i += 1) {
           track[0][i] = i;
        }
        for (let j = 0; j <= str2.length; j += 1) {
           track[j][0] = j;
        }
        for (let j = 1; j <= str2.length; j += 1) {
           for (let i = 1; i <= str1.length; i += 1) {
              const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
              track[j][i] = Math.min(
                 track[j][i - 1] + 1, // deletion
                 track[j - 1][i] + 1, // insertion
                 track[j - 1][i - 1] + indicator, // substitution
              );
           }
        }
        return track[str2.length][str1.length];
    };
}